/**
 * useStaff Hook
 * 
 * Manages staff data fetching and mutations using React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Staff, StaffCreateFromPersonInput, StaffListFilters, StaffPositionFilters } from '../types';
import { 
  getAllStaff, 
  getStaffById, 
  getStaffPositions,
  createStaffPosition,
  deleteStaffPosition,
  getStaffHistory, 
  createStaffFromPerson,
  deleteStaff,
  updateStaff, 
  updateStaffStatus,
  terminateStaff, 
  updateStaffPosition,
} from '../services/staffService';
import { notify } from '@/shared/services';
import { useTranslation } from '@/core';

// Query keys for cache management
export const staffKeys = {
  all: ['staff'] as const,
  lists: () => [...staffKeys.all, 'list'] as const,
  list: (filters?: StaffListFilters) => [...staffKeys.lists(), filters] as const,
  details: () => [...staffKeys.all, 'detail'] as const,
  detail: (id: string) => [...staffKeys.details(), id] as const,
  history: (staffId: string) => [...staffKeys.all, 'history', staffId] as const,
  positions: (filters?: StaffPositionFilters) => [...staffKeys.all, 'positions', filters] as const,
};

/** Hook for fetching staff list with optional filtering */
export function useStaff(filters?: StaffListFilters) {
  return useQuery({
    queryKey: staffKeys.list(filters),
    queryFn: () => getAllStaff(filters),
  });
}

/** Hook for fetching a single staff member by ID */
export function useStaffById(id: string) {
  return useQuery({
    queryKey: staffKeys.detail(id),
    queryFn: () => getStaffById(id),
    enabled: !!id,
  });
}

/** Hook for fetching staff history */
export function useStaffHistory(staffId: string) {
  return useQuery({
    queryKey: staffKeys.history(staffId),
    queryFn: () => getStaffHistory(staffId),
    enabled: !!staffId,
  });
}

/** Hook for fetching staff positions by scope */
export function useStaffPositions(filters?: StaffPositionFilters) {
  return useQuery({
    queryKey: staffKeys.positions(filters),
    queryFn: () => getStaffPositions(filters),
  });
}

/** Hook for creating staff from an existing person */
export function useCreateStaffFromPerson() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (input: StaffCreateFromPersonInput) => createStaffFromPerson(input),
    onSuccess: (createdStaff) => {
      // Invalidate the whole `staff` tree — covers both `staffKeys.lists()`
      // and the `useInfiniteStaff` key `['staff','infinite',…]` the rendered
      // list actually uses (`lists()` is not a prefix of the infinite key).
      queryClient.invalidateQueries({ queryKey: staffKeys.all });
      queryClient.invalidateQueries({ queryKey: staffKeys.detail(createdStaff.id) });
      queryClient.invalidateQueries({ queryKey: ['person', createdStaff.person.id] });
      queryClient.invalidateQueries({ queryKey: ['persons'] });
      queryClient.invalidateQueries({ queryKey: ['person-staff', createdStaff.person.id] });
      notify.success(t('staff.createFlow.success', 'Staff created successfully.'));
    },
    onError: (error: Error) => {
      notify.error(error.message, { description: t('staff.createFlow.failed', 'Could not create the staff record.') });
    },
  });
}

export function useCreateStaffPosition() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: createStaffPosition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffKeys.all });
      notify.success(t('hrShell.positions.messages.created'));
    },
    onError: (error: Error) => {
      notify.error(error.message, { description: t('hrShell.positions.messages.createFailed') });
    },
  });
}

export function useUpdateStaffPosition() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ positionId, input }: { positionId: string; input: Parameters<typeof updateStaffPosition>[1] }) =>
      updateStaffPosition(positionId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffKeys.all });
      notify.success(t('hrShell.positions.messages.updated'));
    },
    onError: (error: Error) => {
      notify.error(error.message, { description: t('hrShell.positions.messages.updateFailed') });
    },
  });
}

export function useDeleteStaffPosition() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (positionId: string) => deleteStaffPosition(positionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffKeys.all });
      notify.success(t('hrShell.positions.messages.deleted'));
    },
    onError: (error: Error) => {
      notify.error(error.message, { description: t('hrShell.positions.messages.deleteFailed') });
    },
  });
}

/** Hook for updating staff data */
export function useUpdateStaff() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ 
      staffId, 
      updates 
    }: { 
      staffId: string; 
      updates: Partial<Omit<Staff, 'id' | 'createdAt' | 'updatedAt'>> 
    }) => updateStaff(staffId, updates),
    onSuccess: (updatedStaff) => {
      // `staffKeys.all` so the rendered `useInfiniteStaff` list refreshes too.
      queryClient.invalidateQueries({ queryKey: staffKeys.all });
      queryClient.invalidateQueries({ queryKey: staffKeys.detail(updatedStaff.id) });
      notify.success(t('staff.updateSuccess', 'Employee updated'));
    },
    onError: (error: Error) => {
      notify.error(error.message, { description: t('staff.updateFailed', 'Could not update the employee record.') });
    },
  });
}

/** Hook for updating staff status */
/**
 * Atomic termination (PENDINGS #22). Invalidates every surface the exit
 * touches: rosters, the person profile, company users and access rows —
 * the backend already forced a permission reload on the terminated user.
 */
export function useTerminateStaff() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ staffId, effectiveDate, reason }: { staffId: string; effectiveDate?: string; reason?: string }) =>
      terminateStaff(staffId, { effectiveDate, reason }),
    onSuccess: ({ staff }) => {
      queryClient.invalidateQueries({ queryKey: staffKeys.all });
      queryClient.invalidateQueries({ queryKey: staffKeys.detail(staff.id) });
      queryClient.invalidateQueries({ queryKey: staffKeys.history(staff.id) });
      queryClient.invalidateQueries({ queryKey: ['company-users'] });
      queryClient.invalidateQueries({ queryKey: ['user-accesses'] });
      queryClient.invalidateQueries({ queryKey: ['person'] });
      notify.success(t('staffActions.terminateSuccess', 'Employment terminated — login, roles and wristbands revoked.'));
    },
    // No onError toast here: the dialog renders the pre-flight reason
    // (open debt / open register) inline, where the operator can act on it.
  });
}

export function useUpdateStaffStatus() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ staffId, status }: { staffId: string; status: Staff['status'] }) => 
      updateStaffStatus(staffId, status),
    onSuccess: (updatedStaff) => {
      // `staffKeys.all` so the rendered `useInfiniteStaff` list refreshes too.
      queryClient.invalidateQueries({ queryKey: staffKeys.all });
      queryClient.invalidateQueries({ queryKey: staffKeys.detail(updatedStaff.id) });
      queryClient.invalidateQueries({ queryKey: staffKeys.history(updatedStaff.id) });
      notify.success(t('staffStatus.updateSuccess', { status: t(`staffStatus.${updatedStaff.status}`) }));
    },
    onError: (error: Error) => {
      notify.error(error.message, { description: t('staffStatus.updateFailed') });
    },
  });
}

export function useDeleteStaff() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (staffId: string) => deleteStaff(staffId),
    onSuccess: () => {
      // `staffKeys.all` so the rendered `useInfiniteStaff` list refreshes too.
      queryClient.invalidateQueries({ queryKey: staffKeys.all });
      notify.success(t('staffActions.deleted', 'Employee deleted'));
    },
    onError: (error: Error) => {
      notify.error(error.message, { description: t('staffActions.deleteFailed', 'Could not delete the employee record.') });
    },
  });
}
